import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  inheritanceClaimsService,
  InheritanceClaimDetail,
} from '../../services/inheritanceClaims.service'
import toast from 'react-hot-toast'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  UNDER_REVIEW: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  PAID: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
}

export default function InheritanceClaimDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [claim, setClaim] = useState<InheritanceClaimDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reviewAction, setReviewAction] = useState<'UNDER_REVIEW' | 'APPROVE' | 'REJECT' | null>(null)
  const [notes, setNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      setClaim(await inheritanceClaimsService.getDetail(id))
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load claim')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const handleReview = async () => {
    if (!claim || !reviewAction) return
    setSaving(true)
    try {
      const updated = await inheritanceClaimsService.review(claim.id, reviewAction, {
        reviewNotes: notes || undefined,
        rejectionReason: reviewAction === 'REJECT' ? rejectionReason : undefined,
      })
      setClaim(updated)
      toast.success(`Claim ${updated.claimNumber} ${updated.status.toLowerCase()}`)
      setReviewAction(null)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save review')
    } finally {
      setSaving(false)
    }
  }

  const handleReviewDirect = async (action: 'UNDER_REVIEW') => {
    if (!claim) return
    try {
      const updated = await inheritanceClaimsService.review(claim.id, action)
      setClaim(updated)
      toast.success(`Claim ${updated.claimNumber} moved to under review`)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update claim')
    }
  }

  const handleApprove = async () => {
    if (!claim) return
    setSaving(true)
    try {
      const updated = await inheritanceClaimsService.review(claim.id, 'APPROVE', {
        reviewNotes: notes || undefined,
      })
      setClaim(updated)
      toast.success(`Approved — payout batch ${updated.payoutBatch?.batchNumber ?? ''} created`)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to approve claim')
    } finally {
      setSaving(false)
    }
  }

  const statusPill = (status: string) => (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
        STATUS_COLORS[status] ?? 'bg-neutral-100 text-neutral-700'
      }`}
    >
      {status.replace('_', ' ')}
    </span>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 py-8">
        <div className="mx-auto max-w-4xl px-6">
          <p className="text-sm text-neutral-500">Loading…</p>
        </div>
      </div>
    )
  }

  if (error || !claim) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Claim not found'}</p>
          <button
            onClick={() => navigate('/admin/claims')}
            className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
          >
            Back to Inheritance Claims
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="mx-auto max-w-4xl px-6">
        <button
          onClick={() => navigate('/admin/claims')}
          className="text-sm text-neutral-500 hover:text-neutral-800"
        >
          ← Back to Inheritance Claims
        </button>

        <div className="mt-3 flex items-center gap-3">
          <h1 className="font-mono text-2xl font-bold text-neutral-900">{claim.claimNumber}</h1>
          {statusPill(claim.status)}
        </div>

        <div className="mt-6 space-y-5 rounded-xl border border-neutral-200 bg-white p-6 text-sm">
          <div>
            <h3 className="mb-2 font-semibold text-neutral-900">Deceased account</h3>
            <p className="text-neutral-700">
              {claim.user.firstName} {claim.user.lastName} · Account {claim.user.memberId} ·
              Wallet {claim.user.wallet?.walletNumber ?? '—'} · Balance{' '}
              {claim.user.wallet ? `RM ${Number(claim.user.wallet.balance).toFixed(2)}` : '—'}
              {claim.user.wallet?.isFrozen ? ' (FROZEN)' : ''}
            </p>
          </div>

          <div>
            <h3 className="mb-2 font-semibold text-neutral-900">Claimant</h3>
            <p className="text-neutral-700">
              {claim.submittedByName} · Member {claim.submittedByMemberId} · NRIC{' '}
              {claim.submittedByNric}
              {claim.relationship ? ` · ${claim.relationship}` : ''}
            </p>
            <p className="text-xs text-neutral-500">
              {claim.submittedByEmail} · {claim.submittedByPhone}
            </p>
            {claim.bankDetails && (
              <p className="text-xs text-neutral-500">
                Claimant bank:{' '}
                {`${claim.bankDetails.bankName ?? ''} · ${claim.bankDetails.accountNumber ?? ''} · ${claim.bankDetails.accountHolderName ?? ''}`}
              </p>
            )}
          </div>

          <div>
            <h3 className="mb-1 font-semibold text-neutral-900">Death certificate</h3>
            <a
              href={claim.deathCertificateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              Open uploaded certificate
            </a>
          </div>

          <div>
            <h3 className="mb-2 font-semibold text-neutral-900">Will beneficiaries</h3>
            <ul className="space-y-1">
              {claim.beneficiaries.map((b) => (
                <li key={b.id} className="text-neutral-700">
                  {b.familyMember.firstName} {b.familyMember.lastName ?? ''} (
                  {b.familyMember.memberId}) — {Number(b.percentage)}%
                  {b.familyMember.isDeceased ? ' · DECEASED' : ''}
                  {b.familyMember.memberId === claim.submittedByMemberId ? ' · CLAIMANT' : ''}
                </li>
              ))}
            </ul>
          </div>

          {claim.status === 'APPROVED' && claim.balanceAtApproval && (
            <p className="rounded-lg bg-emerald-50 p-3 text-emerald-700">
              Approved for RM {Number(claim.balanceAtApproval).toFixed(2)} — payout batch{' '}
              {claim.payoutBatch ? (
                <button
                  onClick={() => navigate(`/admin/claims/payouts/${claim.payoutBatch!.id}`)}
                  className="font-mono underline hover:no-underline"
                >
                  {claim.payoutBatch.batchNumber}
                </button>
              ) : (
                '—'
              )}{' '}
              ({claim.payoutBatch?.status ?? '—'}). Complete the bank transfers offline in the
              payouts area; this claim becomes PAID when the batch completes.
            </p>
          )}
          {claim.rejectionReason && (
            <p className="rounded-lg bg-red-50 p-3 text-red-700">Rejected: {claim.rejectionReason}</p>
          )}

          {reviewAction ? (
            <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
              {reviewAction === 'REJECT' && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-neutral-600">
                    Rejection reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-600">Review notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReview}
                  disabled={saving || (reviewAction === 'REJECT' && rejectionReason.trim().length < 5)}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40"
                >
                  {saving
                    ? 'Saving…'
                    : `Confirm ${reviewAction === 'APPROVE' ? 'Approval' : reviewAction === 'REJECT' ? 'Rejection' : 'Review'}`}
                </button>
                <button
                  onClick={() => setReviewAction(null)}
                  className="rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            (claim.status === 'PENDING' || claim.status === 'UNDER_REVIEW') && (
              <div className="flex gap-2">
                {claim.status === 'PENDING' && (
                  <button
                    onClick={() => handleReviewDirect('UNDER_REVIEW')}
                    className="rounded-md border border-blue-300 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
                  >
                    Start Review
                  </button>
                )}
                <button
                  onClick={handleApprove}
                  className="rounded-md bg-[#0D7330] px-4 py-2 text-sm font-medium text-white hover:bg-[#0a5c27]"
                >
                  Approve & Create Payout
                </button>
                <button
                  onClick={() => setReviewAction('REJECT')}
                  className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Reject
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}
